import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

/** shadcn-style className concat */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/** Convert snake_case keys to camelCase recursively. Ported from frontend-prod. */
export function convertKeysToCamelCase<T>(input: unknown): T {
  if (Array.isArray(input)) {
    return input.map((x) => convertKeysToCamelCase(x)) as unknown as T;
  }
  if (input && typeof input === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(input as Record<string, unknown>)) {
      const camel = k.replace(/_([a-z0-9])/g, (_, c) => c.toUpperCase());
      out[camel] = convertKeysToCamelCase(v);
    }
    return out as T;
  }
  return input as T;
}

/** djb2 hash — small, fast, good enough for doc-change detection */
export function hashString(s: string): string {
  let h = 5381;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  }
  return (h >>> 0).toString(36);
}

/** ArrayBuffer / Uint8Array → base64, chunked to avoid call-stack overflow */
export function bytesToBase64(bytes: Uint8Array): string {
  const CHUNK = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

export function base64ToBytes(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/** Truncate with ellipsis — used for chat titles */
export function truncate(s: string, n = 60): string {
  if (s.length <= n) return s;
  return s.slice(0, n - 1).trimEnd() + '…';
}

/**
 * Minimal markdown → Word-compatible HTML.
 * Ported from frontend-prod/lib/utils.ts:markdownToRichTextWithoutLinks.
 * Used for the Copy button so pasting into Word preserves bold/italic/heading
 * structure rather than showing raw markdown syntax.
 */
export function markdownToRichTextWithoutLinks(markdown: string): string {
  let html = markdown;

  html = html.replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>');
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
  html = html.replace(/__(.+?)__/g, '<strong>$1</strong>');
  html = html.replace(/_(.+?)_/g, '<em>$1</em>');
  html = html.replace(/~~(.+?)~~/g, '<s>$1</s>');

  html = html.replace(
    /`([^`]+)`/g,
    '<span style="font-family: Consolas, Monaco, monospace; background-color: #f4f4f4; padding: 2px 4px; border-radius: 3px;">$1</span>',
  );

  html = html.replace(
    /^### (.+)$/gm,
    '<h3 style="font-size: 1.17em; font-weight: bold; margin: 0.83em 0;">$1</h3>',
  );
  html = html.replace(
    /^## (.+)$/gm,
    '<h2 style="font-size: 1.5em; font-weight: bold; margin: 0.75em 0;">$1</h2>',
  );
  html = html.replace(
    /^# (.+)$/gm,
    '<h1 style="font-size: 2em; font-weight: bold; margin: 0.67em 0;">$1</h1>',
  );

  // Strip markdown links → keep the text only
  html = html.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');

  html = html.replace(/^- (.+)$/gm, '<li style="margin-left: 20px;">$1</li>');
  html = html.replace(/^\d+\. (.+)$/gm, '<li style="margin-left: 20px;">$1</li>');
  html = html.replace(/(<li[^>]*>.*<\/li>\n?)+/g, (match) => {
    return '<ul style="margin: 1em 0; padding-left: 40px;">' + match + '</ul>';
  });

  html = html.replace(/\n\n/g, '</p><p style="margin: 1em 0;">');
  html = html.replace(/\n/g, '<br>');
  html = '<p style="margin: 1em 0;">' + html + '</p>';

  html = html.replace(/<p[^>]*><\/p>/g, '');
  html = html.replace(/<p[^>]*>(<h[123][^>]*>)/g, '$1');
  html = html.replace(/(<\/h[123]>)<\/p>/g, '$1');
  html = html.replace(/<p[^>]*>(<ul[^>]*>)/g, '$1');
  html = html.replace(/(<\/ul>)<\/p>/g, '$1');

  return `<!DOCTYPE html>
<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
<head>
<meta charset="utf-8">
<meta name="ProgId" content="Word.Document">
<meta name="Generator" content="Microsoft Word">
<style>
body { font-family: Calibri, Arial, sans-serif; font-size: 11pt; line-height: 1.5; }
p { margin: 0 0 10pt 0; }
</style>
</head>
<body>
${html}
</body>
</html>`;
}

/** Strip markdown links, keep text. Used as plain-text fallback on copy. */
export function stripMarkdownLinks(markdown: string): string {
  return markdown.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
}
